/**
 * AttachmentBadge — small `📎 N` indicator shown on entity surfaces (BG card,
 * part header, batch row, inspection-event drawer). Patch E26.
 *
 * Zero-count case renders as a faint outlined `📎 +` (clear affordance to
 * attach even when empty). Positive count uses an accent border.
 *
 * Click is wired by the parent — this component is just the visual.
 */
import { Paperclip, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  count: number;
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
}

export function AttachmentBadge({ count, onClick, ariaLabel, className }: Props) {
  const label = ariaLabel ?? (count > 0 ? `${count} attachment${count === 1 ? '' : 's'}` : 'Add attachment');
  const baseStyle =
    'inline-flex items-center gap-0.5 rounded-sm border px-1 py-0.5 text-[10px] font-medium tabular-nums transition-colors';
  const positiveStyle = 'border-accent/40 bg-accent/5 text-accent-strong hover:bg-accent/10';
  const zeroStyle =
    'border-border-default border-dashed text-tertiary hover:border-border-strong hover:text-secondary';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(baseStyle, count > 0 ? positiveStyle : zeroStyle, className)}
    >
      <Paperclip className="h-2.5 w-2.5" strokeWidth={1.5} />
      {count > 0 ? <span>{count}</span> : <Plus className="h-2.5 w-2.5" strokeWidth={1.5} />}
    </button>
  );
}
